import { Injectable, NgZone, inject } from '@angular/core';
import { VwWebsocketService } from 'vwappservices';

type EventHandlers = Record<string, (data: any) => void>;

// Thin wrapper around vwappservices' VwWebsocketService (itself a socket.io-client wrapper) so
// pages don't need to know the connect-before-or-after-handlers ordering it requires.
//
// VwWebsocketService.socketEventHandlers can only hold ONE handler per event name — setEventHandlers()
// just shallow-merges by key, so two independent consumers (e.g. the always-mounted header and
// whichever page is active) registering the same event silently overwrite each other. This wrapper
// is the fix: it keeps its own Set of callbacks per event and fans each incoming message out to all
// of them, always pushing the complete current map down with clearPrevious so VwWebsocketService's
// map can never go stale (its createConnection() rebuilds the socket from that map on every call
// anyway, so this doesn't add extra socket churn beyond what already happened per connect()).
@Injectable({ providedIn: 'root' })
export class LiveUpdatesService {
  private ws = inject(VwWebsocketService);
  private ngZone = inject(NgZone);
  private subscribers = new Map<string, Set<(data: any) => void>>();

  connect(handlers: EventHandlers): () => void {
    for (const key of Object.keys(handlers)) {
      if (!this.subscribers.has(key)) this.subscribers.set(key, new Set());
      this.subscribers.get(key)!.add(handlers[key]);
    }
    this.rebuild();
    return () => this.unsubscribe(handlers);
  }

  private unsubscribe(handlers: EventHandlers) {
    for (const key of Object.keys(handlers)) {
      const set = this.subscribers.get(key);
      set?.delete(handlers[key]);
      if (set && set.size === 0) this.subscribers.delete(key);
    }
    if (this.subscribers.size === 0) {
      this.ws.closeConnection();
    } else {
      this.rebuild();
    }
  }

  private rebuild() {
    const dispatchers: EventHandlers = {};
    for (const [key, callbacks] of this.subscribers) {
      dispatchers[key] = (data) => {
        for (const cb of callbacks) this.ngZone.run(() => cb(data));
      };
    }
    // VwWebsocketService.createConnection() loads socket.io-client via a dynamic import(), which
    // zone.js doesn't track across the await — so its socket event callbacks fire outside Angular's
    // zone. The ngZone.run() above re-enters the zone explicitly for every dispatched callback.
    this.ws.setEventHandlers(dispatchers, true);
    this.ws.createConnection();
  }
}
